<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { getColorFromScore, getHoursMinutesSeconds } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import EditStudentSidePanel from '@/components/SidePanels/EditStudent.vue'
import { examModule } from '@/store/exam/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import ExamVersionDropdown from '@/components/ExamVersionDropdown.vue'

const {
    Button: PocketButton,
    SkeletonLoader,
} = UIKit

const showEditStudentSidePanel = ref(false)
const isLoading = ref(false)

const currentStudentCurrentExam = computed(() => examModule.getters.getCurrentStudentCurrentExam())

const currentStudent = computed(() => studentModule.getters.getCurrentStudent())

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const currentStudentAggStats = computed(() => statsModule.getters.getCurrentStudentAggStats())

const formattedStudentName = computed(() => {
    if (!currentStudent.value) {
        return {
            firstLine: '',
            secondLine: '',
        }
    }

    const studentName = currentStudent.value.displayFirstName || currentStudent.value.displayLastName
        ? [ currentStudent.value.displayFirstName, currentStudent.value.displayLastName ].join(' ')
        : [ currentStudentStats.value.userInfo.firstName, currentStudentStats.value.userInfo.lastName ].join(' ')
    const studentNameWords = studentName.split(' ')
    return studentNameWords.reduce<{
        firstLine: string
        secondLine: string
    }>((acc) => {
        const newFirstLine = `${acc.firstLine}${acc.firstLine ? ' ' : ''}${acc.secondLine.split(' ')[0]}`
        const newSecondLine = acc.secondLine.split(' ').slice(1).join(' ')
        const originalLengthDiff = Math.abs(acc.secondLine.length - acc.firstLine.length)
        const newLengthDiff = Math.abs(newSecondLine.length - newFirstLine.length)
        if (newLengthDiff < originalLengthDiff) {
            acc.firstLine = newFirstLine
            acc.secondLine = newSecondLine
        }

        return acc
    }, {
        firstLine: '',
        secondLine: studentNameWords.join(' '),
    })
})

const formattedTimeStudied = computed(() => {
    const { hours, minutes, seconds } = getHoursMinutesSeconds(currentStudentStats.value.secondsStudied)

    return hours
        ? `${hours}h ${minutes}m`
        : minutes
            ? `${minutes}m ${seconds}s`
            : `${seconds}s`
})

const overallPercentCorrect = computed(() => {
    return Math.round(
        (currentStudentAggStats.value.correctAnswerCount / currentStudentAggStats.value.totalAnswers) * 100
    )
})

const closeEditStudentSidePanel = () => {
    showEditStudentSidePanel.value = false
}

const openEditStudentSidePanel = () => {
    showEditStudentSidePanel.value = true
}

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        examModule.actions.fetchCurrentStudentCurrentExam(),
        studentModule.actions.fetchCurrentStudent(),
        statsModule.actions.fetchCurrentStudentStats(),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])
    isLoading.value = false
})
</script>

<template>
    <div>
        <EditStudentSidePanel
            v-if="showEditStudentSidePanel"
            :org-student-id="currentStudent && currentStudent.objectId || ''"
            @close="closeEditStudentSidePanel"
        />
        <div class="student-stats-head">
            <div v-if="currentStudentCurrentExam" class="student-stats-head__exams">
                <ExamVersionDropdown />
            </div>
            <PocketButton
                v-if="!currentStudentStats.isLoading"
                class="student-stats-head__edit-student-btn"
                type="tertiary"
                @click="openEditStudentSidePanel"
            >
                Edit Student
            </PocketButton>
        </div>
        <div class="student-stats-head__stats">
            <div class="student-stats-head__stats-name">
                <SkeletonLoader
                    v-if="currentStudentStats.isLoading"
                    class="student-stats-head__loader-name"
                />
                <div
                    v-if="!currentStudentStats.isLoading"
                >
                    {{ formattedStudentName.firstLine }}
                </div>
                <SkeletonLoader
                    v-if="currentStudentStats.isLoading"
                    class="student-stats-head__loader-name"
                />
                <div
                    v-if="!currentStudentStats.isLoading"
                >
                    {{ formattedStudentName.secondLine }}
                </div>
            </div>
            <div class="student-stats-head__stats-summary">
                <div
                    class="student-stats-head__stats-overall student-stats-head__stats-stat"
                    :class="
                        !currentStudentStats.isLoading ? getColorFromScore(overallPercentCorrect) : undefined"
                >
                    <SkeletonLoader
                        v-if="currentStudentStats.isLoading"
                        class="student-stats-head__loader"
                    />
                    <span
                        v-if="!currentStudentStats.isLoading"
                    >
                        {{ overallPercentCorrect || 0 }}%
                    </span>
                    <label>Overall Score</label>
                </div>
                <div class="student-stats-head__stats-correct student-stats-head__stats-stat">
                    <SkeletonLoader
                        v-if="currentStudentStats.isLoading"
                        class="student-stats-head__loader"
                    />
                    <span
                        v-if="!currentStudentStats.isLoading"
                    >
                        {{ currentStudentAggStats.correctAnswerCount }} / {{ currentStudentAggStats.totalAnswers }}
                    </span>
                    <label>Correct of Answered</label>
                </div>
                <div class="student-stats-head__stats-unanswered student-stats-head__stats-stat">
                    <SkeletonLoader
                        v-if="currentStudentStats.isLoading"
                        class="student-stats-head__loader"
                    />
                    <span
                        v-if="!currentStudentStats.isLoading"
                    >
                        {{ currentStudentAggStats.totalQuestions - currentStudentAggStats.totalAnswers }}
                    </span>
                    <label>Unanswered</label>
                </div>
                <div class="student-stats-head__stats-time student-stats-head__stats-stat" title="HH:MM">
                    <SkeletonLoader
                        v-if="currentStudentStats.isLoading"
                        class="student-stats-head__loader"
                    />
                    <span
                        v-if="!currentStudentStats.isLoading"
                    >
                        {{ formattedTimeStudied }}
                    </span>
                    <label>Time Spent Studying</label>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.student-stats-head {
    display: flex;
    font-size: 14px;
    justify-content: space-between;
    margin-bottom: 24px;
    position: relative;

    &__edit-student-btn {
        padding: 0 !important;
    }

    &__stats {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        label {
            font-size: 12px;
            line-height: 15.71px;
            display: block;
            color: $slate-01;
        }
    }

    &__stats-name {
        max-width: 320px;
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        overflow-wrap: break-word;
    }

    &__stats-summary {
        display: flex;
    }

    &__loader {
        height: 24px;
        width: 60px;
        margin: 2px auto 8px auto;
    }

    &__loader-name {
        width: 160px;
        height: 24px;
        margin-bottom: 10px;
    }

    &__stats-stat {
        font-size: 24px;
        text-align: center;
        letter-spacing: 0.24px;
        flex: 1;
        margin: 0 15px;

        &:last-child {
            margin-right: 0;
        }

        span {
            font-weight: 600;
            white-space: nowrap;
        }

        label {
            white-space: nowrap;
        }
    }

    &__stats-overall {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: -5px;
            width: 94px;
            height: 44px;
            margin-left: -52px;
        }

        &.green::before {
            background: url('@/assets/images/highlight-green.svg') no-repeat center center;
        }

        &.red::before {
            background: url('@/assets/images/highlight-red.svg') no-repeat center center;
        }

        &.yellow::before {
            background: url('@/assets/images/highlight-yellow.svg') no-repeat center center;
        }
    }
}
</style>
